import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import Waveform from "./Waveform";
import YouTubePlayer from "./YouTubePlayer";
import { Play, Pause, RotateCcw, Zap, MoreHorizontal, Flag } from "lucide-react";
import { toast } from "sonner";

const Deck = forwardRef(function Deck({ side = "A", track, faderVolume = 1, onNearEnd, onEnd, onBpmChange, onMarkDropEnd }, ref) {
  const isA = side === "A";
  const color = isA ? "#d946ef" : "#06b6d4";
  const label = isA ? "DECK A" : "DECK B";

  const playerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [pitch, setPitch] = useState(1);
  const [expanded, setExpanded] = useState(false);
  const [diag, setDiag] = useState("boot");
  const [ytErr, setYtErr] = useState(null);
  const notifiedNearEnd = useRef(false);

  const setVolume = (v) => {
    try {
      if (playerRef.current && playerRef.current.setVolume) {
        playerRef.current.setVolume(Math.max(0, Math.min(100, Math.round(v * 100))));
      }
    } catch (_) {}
  };

  useEffect(() => {
    setVolume(Math.max(0, faderVolume));
  }, [faderVolume]);

  // Near-end polling fallback. YT's infoDelivery pushes stop for a beat
  // right after a seek — polling the controller keeps Auto-DJ from
  // missing the T-20s trigger when the user manually scrubs into the
  // last stretch of a track.
  useEffect(() => {
    if (!track || !playing) return undefined;
    const iv = setInterval(() => {
      try {
        const p = playerRef.current;
        if (!p) return;
        const t = p.getCurrentTime ? p.getCurrentTime() : 0;
        const d = p.getDuration ? p.getDuration() : 0;
        if (d > 0 && t > 0 && d - t <= 20 && !notifiedNearEnd.current && onNearEnd) {
          notifiedNearEnd.current = true;
          onNearEnd(side, d - t);
        }
      } catch (_e) { /* controller not yet mounted */ }
    }, 400);
    return () => clearInterval(iv);
  }, [track, playing, onNearEnd, side]);

  useEffect(() => {
    notifiedNearEnd.current = false;
    if (!track) return;
    // If the YT player is ready, cue immediately. Otherwise the onReady
    // handler will pick up the pending track and cue it there.
    if (playerRef.current && playerRef.current.cueVideoById) {
      try {
        playerRef.current.cueVideoById(track.video_id);
        setPlaying(false);
        setProgress(0);
        setCurrent(0);
      } catch (_) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track?.video_id]);

  useImperativeHandle(ref, () => ({
    play: () => {
      if (!playerRef.current || !track) return;
      try { playerRef.current.playVideo(); } catch (_) {}
    },
    pause: () => {
      try { playerRef.current && playerRef.current.pauseVideo(); } catch (_) {}
    },
    isPlaying: () => playing,
    hasTrack: () => !!track,
    getPitch: () => pitch,
    getCurrentTime: () => {
      try {
        return playerRef.current && playerRef.current.getCurrentTime
          ? playerRef.current.getCurrentTime()
          : 0;
      } catch (_) { return 0; }
    },
    getDuration: () => duration,
    getPlayerState: () => {
      try {
        return playerRef.current && playerRef.current.getPlayerState
          ? playerRef.current.getPlayerState()
          : -1;
      } catch (_) { return -1; }
    },
    setRate: (r) => {
      const v = Math.max(0.5, Math.min(1.5, Number(r) || 1));
      setPitch(v);
      try {
        playerRef.current && playerRef.current.setPlaybackRate && playerRef.current.setPlaybackRate(v);
      } catch (_) {}
    },
  }));

  const togglePlay = () => {
    if (!playerRef.current || !track) {
      setDiag(!playerRef.current ? "no player" : "no track");
      return;
    }
    setYtErr(null);
    try {
      if (playing) {
        playerRef.current.pauseVideo();
        setDiag("pause tapped");
        return;
      }
      // playVideo now reloads the iframe URL with autoplay=1&mute=1 which
      // is the only path browsers accept. Auto-unmute happens on state=1.
      setDiag("play tapped");
      playerRef.current.playVideo();
    } catch (_e) {
      // no-op — errors surface via onError
    }
  };

  const cue = () => {
    if (!playerRef.current) return;
    try {
      playerRef.current.seekTo(0, true);
      playerRef.current.pauseVideo();
      setProgress(0);
      setCurrent(0);
    } catch (_) {}
  };

  const bpm = track?.bpm || 120;
  const pitchLabel = Math.round((pitch - 1) * 100);
  const bpmManual = !!track?.bpm_manual;

  // Tap tempo
  const tapsRef = useRef([]);
  const [tapPulse, setTapPulse] = useState(0);
  const handleTap = () => {
    const now = performance.now();
    const arr = tapsRef.current.filter((t) => now - t < 3000);
    arr.push(now);
    tapsRef.current = arr;
    setTapPulse((p) => p + 1);
    if (arr.length >= 2) {
      const intervals = [];
      for (let i = 1; i < arr.length; i++) intervals.push(arr[i] - arr[i - 1]);
      const avg = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      const detected = Math.round(60000 / avg);
      const clamped = Math.max(60, Math.min(200, detected));
      if (track && onBpmChange) onBpmChange(track.song_uid, clamped);
    }
  };
  const resetTap = () => {
    tapsRef.current = [];
    setTapPulse(0);
  };

  return (
    <div
      className={`deck-frame-${side.toLowerCase()} ${playing ? "active" : ""} rounded-2xl p-3 relative overflow-hidden`}
      data-testid={`deck-${side.toLowerCase()}`}
    >
      {/* Header: label · title · advanced toggle */}
      <div className="flex items-center gap-2 mb-2">
        <div className="w-2 h-2 rounded-full shrink-0" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
        <span className="font-mono text-[9px] tracking-[0.25em] font-bold shrink-0" style={{ color }}>{label}</span>
        <span
          className="font-mono text-[8px] tracking-widest uppercase px-1.5 py-0.5 rounded truncate max-w-[110px]"
          style={{
            color: ytErr ? "#ef4444" : "#71717a",
            background: ytErr ? "rgba(239,68,68,0.10)" : "rgba(63,63,70,0.30)",
            border: `1px solid ${ytErr ? "rgba(239,68,68,0.45)" : "rgba(82,82,91,0.35)"}`,
          }}
          title={ytErr ? `YouTube error ${ytErr} · try another track` : `player state: ${diag}`}
          data-testid={`deck-${side.toLowerCase()}-status`}
        >
          {ytErr ? `ERR ${ytErr}` : diag}
        </span>
        <button
          onClick={() => setExpanded((v) => !v)}
          disabled={!track}
          className="ml-auto p-1 rounded text-zinc-500 hover:text-white hover:bg-white/10 disabled:opacity-30"
          data-testid={`expand-deck-${side.toLowerCase()}`}
          title={expanded ? "Hide advanced" : "Show advanced"}
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </div>

      {/* Song title (1 line, truncated) */}
      <div
        className="font-heading font-bold text-sm leading-tight truncate mb-2"
        data-testid={`deck-${side.toLowerCase()}-title`}
        title={track?.title || ""}
      >
        {track?.title || "— No track —"}
      </div>

      {/* Waveform + times */}
      <div className="mb-2">
        <Waveform
          videoId={track?.video_id}
          progress={progress}
          color={color}
          active={playing}
          markerFrac={
            track?.drop_end_seconds && duration > 0
              ? Math.min(1, track.drop_end_seconds / duration)
              : null
          }
          onSeek={(frac) => {
            if (!playerRef.current || !track) return;
            try {
              const d = playerRef.current.getDuration && playerRef.current.getDuration();
              if (d && d > 0) {
                playerRef.current.seekTo(d * frac, true);
                setProgress(frac);
                setCurrent(d * frac);
                // Reset the near-end flag on every seek so scrubbing into
                // (or out of) the last-20s zone can re-arm Auto-DJ.
                notifiedNearEnd.current = false;
              }
            } catch (_) {}
          }}
        />
        <div className="flex justify-between font-mono text-[9px] text-zinc-500 mt-0.5">
          <span>{fmtTime(current)}</span>
          <span>-{fmtTime(Math.max(0, duration - current))}</span>
        </div>
      </div>

      {/* Big PLAY button */}
      <button
        onClick={togglePlay}
        disabled={!track}
        data-testid={`play-deck-${side.toLowerCase()}`}
        className="w-full rounded-xl py-3.5 font-mono text-sm font-black tracking-[0.2em] border-2 transition-all active:scale-[0.98] disabled:opacity-30 disabled:cursor-not-allowed"
        style={{
          background: playing ? color : "#0a0a0a",
          borderColor: playing ? color : `${color}66`,
          color: playing ? "#050505" : color,
          boxShadow: playing ? `0 0 24px ${color}66` : "none",
        }}
      >
        {playing ? <Pause className="inline w-5 h-5 mr-1.5" fill="currentColor" /> : <Play className="inline w-5 h-5 mr-1.5" fill="currentColor" />}
        {playing ? "PAUSE" : "PLAY"}
      </button>

      {/* Advanced controls — collapsible drawer */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-zinc-800 space-y-2" data-testid={`deck-${side.toLowerCase()}-advanced`}>
          <div className="flex items-center gap-2 text-[10px] text-zinc-400">
            <span className="font-mono truncate flex-1">{track?.channel || ""}</span>
            <span className="font-mono font-black text-sm" style={{ color }}>
              {bpm}
              <span className="text-[8px] text-zinc-500 ml-0.5">BPM{bpmManual ? "" : "~"}</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleTap}
              onDoubleClick={resetTap}
              disabled={!track}
              className="px-2 py-1 rounded text-[9px] font-mono font-bold tracking-widest border transition-all disabled:opacity-30 active:scale-95"
              style={{
                color,
                borderColor: `${color}55`,
                background: tapPulse ? `${color}22` : "transparent",
              }}
              data-testid={`tap-tempo-${side.toLowerCase()}`}
            >
              <Zap className="inline w-3 h-3 mr-0.5" />
              TAP
            </button>
            <button
              onClick={cue}
              disabled={!track}
              data-testid={`cue-deck-${side.toLowerCase()}`}
              className="px-2 py-1 rounded text-[9px] font-mono font-bold tracking-widest border bg-zinc-950 border-zinc-800 disabled:opacity-30"
            >
              <RotateCcw className="inline w-3 h-3 mr-0.5" /> CUE
            </button>
            <button
              onClick={() => {
                if (!track || !onMarkDropEnd) return;
                const t = playerRef.current && playerRef.current.getCurrentTime
                  ? playerRef.current.getCurrentTime()
                  : 0;
                onMarkDropEnd(track.song_uid, Math.max(0, t));
              }}
              disabled={!track}
              className="px-2 py-1 rounded text-[9px] font-mono font-bold tracking-widest border transition-all disabled:opacity-30 active:scale-95"
              style={{
                color: track?.drop_end_seconds ? "#eab308" : "#a1a1aa",
                borderColor: track?.drop_end_seconds ? "#eab308aa" : "#3f3f46",
                background: track?.drop_end_seconds ? "rgba(234,179,8,0.10)" : "transparent",
              }}
              title="Tap when the drop ends — Auto-DJ will wait for this before transitioning."
              data-testid={`mark-drop-${side.toLowerCase()}`}
            >
              <Flag className="inline w-3 h-3 mr-0.5" />
              {track?.drop_end_seconds ? `DROP ${fmtTime(track.drop_end_seconds)}` : "MARK DROP"}
            </button>
            <span className="font-mono text-[9px] text-zinc-500 shrink-0">
              PITCH {pitchLabel >= 0 ? "+" : ""}{pitchLabel}%
            </span>
            <input
              type="range"
              className={`pitch pitch-${side.toLowerCase()} flex-1 min-w-0`}
              min="0.5" max="1.5" step="0.01"
              value={pitch}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                setPitch(v);
                try { playerRef.current && playerRef.current.setPlaybackRate(v); } catch (_) {}
              }}
              data-testid={`pitch-deck-${side.toLowerCase()}`}
            />
          </div>
        </div>
      )}

      <YouTubePlayer
        videoId={track?.video_id}
        slot={side === "A" ? 0 : 1}
        onDiag={(msg) => setDiag(msg)}
        onError={(code) => {
          setYtErr(code);
          setDiag(`YT err ${code}`);
          const msg =
            code === 2 ? "Invalid video ID"
            : code === 5 ? "Video can't play here (HTML5 error)"
            : code === 100 ? "Video removed or private"
            : code === 101 || code === 150 ? "Owner blocked embedding — try a different upload of this song"
            : code === -1 ? "YouTube blocked by browser or network"
            : `YouTube error ${code}`;
          toast.error(`Deck ${side}: ${msg}`, { duration: 5000 });
        }}
        onReady={(p) => {
          playerRef.current = p;
          try { p.setVolume(Math.round(faderVolume * 100)); } catch (_) {}
          // Do NOT call cueVideoById here — the YouTubePlayer already
          // loaded the iframe with the correct videoId (and autoplay=1
          // if the user tapped PLAY). Cueing again would reset autoplay.
          setDiag("ready");
        }}
        onStateChange={(state) => {
          if (state === 1) {
            setPlaying(true);
            setDiag("playing");
            try {
              if (playerRef.current && playerRef.current.unMute) {
                playerRef.current.unMute();
                playerRef.current.setVolume(Math.round(Math.max(0, faderVolume) * 100));
              }
            } catch (_) {}
          }
          if (state === 2) { setPlaying(false); setDiag("paused"); }
          if (state === 3) setDiag("buffering");
          if (state === 5) setDiag("cued");
          if (state === 0) {
            setPlaying(false);
            setProgress(0);
            setDiag("ended");
            onEnd && onEnd(side);
          }
        }}
        onTime={(t, d) => {
          setCurrent(t); setDuration(d);
          if (d > 0) {
            setProgress(t / d);
            if (!notifiedNearEnd.current && d - t <= 20 && t > 0 && onNearEnd) {
              notifiedNearEnd.current = true;
              onNearEnd(side, d - t);
            }
          }
        }}
      />
    </div>
  );
});

function fmtTime(s) {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export default Deck;
